import React, { useEffect, useState } from 'react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Dialog } from '@/components/ui/Modal';
import { PageContainer } from '@/components/ui/PageContainer';
import { proposalService, type EventLibraryItem, type EventTag, type ImageObject } from '@/services/ProposalService';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { AttachmentsService } from '@/services/AttachmentsService';
import {
  Plus,
  Pencil,
  Trash2,
  Image as ImageIcon,
  Upload,
  Loader2,
  CalendarDays,
  X,
} from 'lucide-react';

const TAG_OPTIONS: { value: EventTag; label: string; emoji: string }[] = [
  { value: 'off_venue', label: 'Off-Venue', emoji: '🏛️' },
  { value: 'welcome_reception', label: 'Welcome Reception', emoji: '🥂' },
  { value: 'final_dinner', label: 'Final Dinner', emoji: '🍽️' },
  { value: 'nightlife', label: 'Nightlife', emoji: '🌙' },
];

function getTagInfo(tag: EventTag | null) {
  return TAG_OPTIONS.find(t => t.value === tag);
}

export function EventLibrary() {
  const notifySuccess = useNotificationStore(s => s.success);
  const notifyError = useNotificationStore(s => s.error);
  const { hasAnyPermission } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<EventLibraryItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState<EventTag | ''>('');

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EventLibraryItem | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formTag, setFormTag] = useState<EventTag | ''>('');
  const [formPrice, setFormPrice] = useState('');
  const [formParticipants, setFormParticipants] = useState('');
  const [formImages, setFormImages] = useState<ImageObject[]>([]);
  const [uploading, setUploading] = useState(false);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const canManage = hasAnyPermission([
    PERMISSIONS.MANAGE_DESTINATIONS,
    PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS,
    PERMISSIONS.MANAGE_EVENTS,
  ]);

  async function loadItems() {
    setLoading(true);
    try {
      const list = await proposalService.listEventLibrary(true);
      setItems(list);
    } catch (e: any) {
      notifyError(e?.message || 'Failed to load event library');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadItems(); }, []);

  function openCreate() {
    setEditingItem(null);
    setFormName('');
    setFormDescription('');
    setFormTag('');
    setFormPrice('');
    setFormParticipants('');
    setFormImages([]);
    setEditorOpen(true);
  }

  function openEdit(item: EventLibraryItem) {
    setEditingItem(item);
    setFormName(item.name);
    setFormDescription(item.description || '');
    setFormTag(item.tag || '');
    setFormPrice(item.estimated_price_per_person != null ? String(item.estimated_price_per_person) : '');
    setFormParticipants(item.participants || '');
    setFormImages(item.images || []);
    setEditorOpen(true);
  }

  async function handleSave() {
    if (!formName.trim()) {
      notifyError('Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        tag: (formTag || undefined) as EventTag | undefined,
        estimated_price_per_person: formPrice ? Number(formPrice) : undefined,
        participants: formParticipants.trim() || undefined,
        images: formImages,
      };

      if (editingItem) {
        await proposalService.updateEventLibraryItem(editingItem.id, payload);
        notifySuccess('Event updated');
      } else {
        await proposalService.createEventLibraryItem(payload);
        notifySuccess('Event created');
      }
      setEditorOpen(false);
      await loadItems();
    } catch (e: any) {
      notifyError(e?.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await proposalService.deleteEventLibraryItem(id);
      notifySuccess('Event deleted');
      setDeleteConfirmId(null);
      await loadItems();
    } catch (e: any) {
      notifyError(e?.message || 'Failed to delete event');
    }
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const svc = new AttachmentsService();
    try {
      for (const file of Array.from(files)) {
        const attachment = await svc.upload(file, {
          tags: ['event_library'],
        });
        setFormImages(prev => [
          ...prev,
          {
            url: attachment.url,
            attachment_id: attachment.id,
            caption: '',
            sort_order: prev.length,
          },
        ]);
      }
    } catch (err: any) {
      notifyError(err?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  function removeImage(index: number) {
    setFormImages(prev => prev.filter((_, i) => i !== index));
  }

  // Filter and sort
  const filtered = items
    .filter(item => {
      if (!item.is_active) return false;
      if (tagFilter && item.tag !== tagFilter) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return (
          item.name.toLowerCase().includes(q) ||
          (item.description && item.description.toLowerCase().includes(q))
        );
      }
      return true;
    })
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));

  return (
    <PageContainer>
      <PageHeader
        title="Event Library"
        description="Reusable event templates for proposals — not tied to a specific destination"
      >
        {canManage && (
          <Button size="sm" onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
            New Event
          </Button>
        )}
      </PageHeader>

      {/* Search & Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Input
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
          />
        </div>
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value as EventTag | '')}
        >
          <option value="">All Tags</option>
          {TAG_OPTIONS.map(t => (
            <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="mb-6 text-sm text-gray-500">
        {filtered.length} event{filtered.length !== 1 ? 's' : ''}
        {searchTerm && ` matching "${searchTerm}"`}
        {tagFilter && ` tagged "${getTagInfo(tagFilter)?.label}"`}
      </div>

      {/* Content */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <CalendarDays className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || tagFilter ? 'No events found' : 'No events yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || tagFilter
                ? 'Try adjusting your filters'
                : 'Create your first reusable event template'}
            </p>
            {!searchTerm && !tagFilter && canManage && (
              <Button onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
                New Event
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item) => {
            const tagInfo = getTagInfo(item.tag);
            const previewImage = item.images?.[0]?.url;

            return (
              <Card
                key={item.id}
                className="group relative overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-200"
                onClick={() => canManage && openEdit(item)}
              >
                {/* Preview Image */}
                {previewImage ? (
                  <div className="h-32 bg-gray-100 overflow-hidden">
                    <img
                      src={previewImage}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-32 bg-gray-100 flex items-center justify-center">
                    <CalendarDays className="w-12 h-12 text-gray-300" />
                  </div>
                )}

                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-medium text-gray-900 leading-tight">{item.name}</h3>
                    {tagInfo && (
                      <Badge variant="secondary" className="text-xs whitespace-nowrap">
                        {tagInfo.emoji} {tagInfo.label}
                      </Badge>
                    )}
                  </div>

                  {item.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{item.description}</p>
                  )}

                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.estimated_price_per_person != null && (
                      <Badge variant="secondary" className="text-xs">
                        ${item.estimated_price_per_person}/person
                      </Badge>
                    )}
                    {item.participants && (
                      <Badge variant="secondary" className="text-xs">
                        {item.participants} participants
                      </Badge>
                    )}
                    {item.images && item.images.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        <ImageIcon className="w-3 h-3 mr-1" />
                        {item.images.length}
                      </Badge>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {canManage && (
                    <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); openEdit(item); }}
                        className="text-gray-600 hover:text-primary-600 hover:bg-primary-50"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(item.id); }}
                        className="text-gray-600 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation */}
      <Dialog
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Event"
        description="This will remove the event from your library. Events already added to proposals won't be affected."
      >
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}>
            Delete
          </Button>
        </div>
      </Dialog>

      {/* Create / Edit Modal */}
      <Dialog
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editingItem ? 'Edit Event' : 'New Event'}
        className="max-w-xl"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <Input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g., Casino Royale Night"
              required
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[80px] resize-y"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Describe the event..."
            />
          </div>

          {/* Tag */}
          <div>
            <label className="block text-sm font-medium mb-1">Tag</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              value={formTag}
              onChange={(e) => setFormTag(e.target.value as EventTag | '')}
            >
              <option value="">No tag</option>
              {TAG_OPTIONS.map(t => (
                <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>
              ))}
            </select>
          </div>

          {/* Price & Participants */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Price / Person ($)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formPrice}
                onChange={(e) => setFormPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Participants</label>
              <Input
                value={formParticipants}
                onChange={(e) => setFormParticipants(e.target.value)}
                placeholder="e.g., everyone, 50, 25-30"
              />
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium mb-1">Images</label>
            {formImages.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formImages.map((img, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded overflow-hidden group">
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer text-sm text-gray-600 hover:bg-gray-50">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading...' : 'Upload Images'}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageUpload}
                disabled={uploading}
              />
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving || !formName.trim()}>
              {saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Dialog>
    </PageContainer>
  );
}
